import { onBeforeUnmount, onMounted, ref, watch, type Ref } from 'vue'
import type { MessagePhase, StoreMessage } from '@/lib/messageSlots'

// Keys that scroll the container from the keyboard — used to detect that the
// user deliberately moved away while a message was streaming.
const CANCEL_SCROLL_KEYS = new Set([
  'PageUp', 'PageDown', 'ArrowUp', 'ArrowDown', 'Home', 'End', ' ', 'Spacebar',
])

/**
 * One-shot, event-driven auto-scroll for the conversation message list.
 *
 * Design contract:
 * - There is NO "follow" behavior. Each scroll is a single animated move that
 *   fires at most once per message/alternative and then stops.
 * - User intent to opt out is read from input EVENTS (touch/wheel/key), never
 *   from scroll geometry. Any input between arming and firing disarms the pin;
 *   any input during a tween cancels it. The intended failure mode is to NOT
 *   scroll, so these checks are deliberately sensitive.
 */

const TWEEN_MS = 320
const INITIAL_JUMP_ATTEMPTS = 3
// Pending message's top rests this far above the bottom fold on the reveal.
const REVEAL_MARGIN_PX = 96
// Pin leaves this gap between the message top and the viewport top.
const PIN_TOP_OFFSET_PX = 16
// Tween aborts if observed scrollTop drifts from what we wrote by more than this.
const EXTERNAL_DRIFT_PX = 12

interface AlternativeLike {
  id: number | string
  content?: string
  phase?: MessagePhase
}

interface UseMessageAutoScrollOptions {
  container: Ref<HTMLElement | null>
  getMessages: () => StoreMessage[]
  /** Streaming reroll alternatives, keyed by parent message id. Optional. */
  getAlternatives?: () => Record<number, AlternativeLike[]>
}

function easeOutCubic(t: number): number {
  return 1 - Math.pow(1 - t, 3)
}

export function useMessageAutoScroll({
  container,
  getMessages,
  getAlternatives,
}: UseMessageAutoScrollOptions) {
  // Ids we have already resolved (fired or deliberately skipped). One-shot latch.
  const handledIds = new Set<string>()
  // Alternatives we have already seen appear (used to arm on reroll).
  const seenAltIds = new Set<string>()
  let initialized = false
  let armed = false
  // True when the previous append was a user send, so the AI placeholder that
  // follows belongs to a normal send (and must respect an intervening disarm)
  // rather than a resend/regenerate that needs its own arm.
  let lastAppendWasUserSend = false

  let rafId: number | null = null
  let initialJumpRafId: number | null = null
  let tweenCleanup: (() => void) | null = null
  let disarmCleanup: (() => void) | null = null

  const el = () => container.value

  // --- Sticky "user cancelled auto-scroll" signal --------------------------
  // Independent of the one-shot pin engine: whenever the user scrolls the
  // container while a message is streaming, that message's key is marked and
  // never cleared (until the message leaves the list). Hosts read this to keep
  // things like a streaming reasoning panel expanded instead of auto-collapsing.
  const cancelledScrollKeys = ref<string[]>([])

  function messageKey(m: StoreMessage): string {
    const key = m.client_key ?? m.id
    return String(key)
  }

  function markStreamingScrollCancelled(): void {
    const streaming = streamingMessage(getMessages())
    if (!streaming) return
    const key = messageKey(streaming)
    if (!cancelledScrollKeys.value.includes(key)) {
      cancelledScrollKeys.value = [...cancelledScrollKeys.value, key]
    }
  }

  function onCancelKeyDown(e: KeyboardEvent): void {
    if (CANCEL_SCROLL_KEYS.has(e.key)) markStreamingScrollCancelled()
  }

  function wasAutoScrollCancelled(m: StoreMessage): boolean {
    return cancelledScrollKeys.value.includes(messageKey(m))
  }

  function findMessageEl(id: string | number): HTMLElement | null {
    const c = el()
    if (!c) return null
    const key = String(id)
    const nodes = c.querySelectorAll<HTMLElement>('[data-message-id]')
    for (const node of nodes) {
      if (node.getAttribute('data-message-id') === key) return node
    }
    return null
  }

  // --- Arm / disarm -------------------------------------------------------

  function disarm() {
    armed = false
    disarmCleanup?.()
    disarmCleanup = null
  }

  function arm() {
    disarm()
    armed = true
    const c = el()
    if (!c) return
    const onInput = () => disarm()
    c.addEventListener('touchstart', onInput, { passive: true })
    c.addEventListener('wheel', onInput, { passive: true })
    c.addEventListener('keydown', onInput)
    disarmCleanup = () => {
      c.removeEventListener('touchstart', onInput)
      c.removeEventListener('wheel', onInput)
      c.removeEventListener('keydown', onInput)
    }
  }

  // --- Tween engine -------------------------------------------------------

  function cancelTween() {
    if (rafId != null) {
      cancelAnimationFrame(rafId)
      rafId = null
    }
    tweenCleanup?.()
    tweenCleanup = null
  }

  function cancelInitialJump() {
    if (initialJumpRafId != null) {
      cancelAnimationFrame(initialJumpRafId)
      initialJumpRafId = null
    }
  }

  function tweenTo(targetTop: number) {
    cancelTween()
    const c = el()
    if (!c) return

    const start = c.scrollTop
    const maxTop = Math.max(0, c.scrollHeight - c.clientHeight)
    const target = Math.max(0, Math.min(targetTop, maxTop))
    const delta = target - start
    if (Math.abs(delta) < 1) return

    const startTime = performance.now()
    let lastWritten = start

    const onInterrupt = () => cancelTween()
    const onScroll = () => {
      // A scroll we didn't write means an external actor (fling, scrollbar,
      // OS gesture) moved the container — yield to the user.
      if (Math.abs(c.scrollTop - lastWritten) > EXTERNAL_DRIFT_PX) cancelTween()
    }

    c.addEventListener('touchstart', onInterrupt, { passive: true })
    c.addEventListener('wheel', onInterrupt, { passive: true })
    c.addEventListener('keydown', onInterrupt)
    c.addEventListener('scroll', onScroll, { passive: true })

    tweenCleanup = () => {
      c.removeEventListener('touchstart', onInterrupt)
      c.removeEventListener('wheel', onInterrupt)
      c.removeEventListener('keydown', onInterrupt)
      c.removeEventListener('scroll', onScroll)
    }

    const step = (now: number) => {
      const t = Math.min(1, (now - startTime) / TWEEN_MS)
      lastWritten = start + delta * easeOutCubic(t)
      c.scrollTop = lastWritten
      if (t < 1) {
        rafId = requestAnimationFrame(step)
      } else {
        rafId = null
        tweenCleanup?.()
        tweenCleanup = null
      }
    }
    rafId = requestAnimationFrame(step)
  }

  // --- Scroll primitives --------------------------------------------------

  /** True when the element sits at least partly within the container viewport. */
  function isInView(target: HTMLElement): boolean {
    const c = el()
    if (!c) return false
    const cRect = c.getBoundingClientRect()
    const tRect = target.getBoundingClientRect()
    return tRect.bottom > cRect.top && tRect.top < cRect.bottom
  }

  function pinToTop(target: HTMLElement) {
    const c = el()
    if (!c) return
    const cRect = c.getBoundingClientRect()
    const tRect = target.getBoundingClientRect()
    tweenTo(c.scrollTop + (tRect.top - cRect.top) - PIN_TOP_OFFSET_PX)
  }

  /** Rise the element's top to `margin` above the bottom fold — minimal reveal. */
  function revealTop(target: HTMLElement, margin: number) {
    const c = el()
    if (!c) return
    const cRect = c.getBoundingClientRect()
    const tRect = target.getBoundingClientRect()
    const bottomInset = parseFloat(getComputedStyle(c).paddingBottom) || 0
    const delta = tRect.top - (cRect.bottom - bottomInset - margin)
    if (delta <= 0) return
    tweenTo(c.scrollTop + delta)
  }

  function jumpTopOf(target: HTMLElement) {
    const c = el()
    if (!c) return
    const cRect = c.getBoundingClientRect()
    const tRect = target.getBoundingClientRect()
    c.scrollTop = Math.max(0, c.scrollTop + (tRect.top - cRect.top) - PIN_TOP_OFFSET_PX)
  }

  function scheduleInitialJumpTo(id: string | number) {
    cancelInitialJump()

    let attemptsLeft = INITIAL_JUMP_ATTEMPTS
    const run = () => {
      initialJumpRafId = null
      const target = findMessageEl(id)
      if (target) jumpTopOf(target)

      attemptsLeft -= 1
      if (attemptsLeft > 0) {
        initialJumpRafId = requestAnimationFrame(run)
      }
    }

    run()
  }

  // --- Watchers -----------------------------------------------------------

  function lastMessage(list: StoreMessage[]): StoreMessage | undefined {
    return list.length ? list[list.length - 1] : undefined
  }

  function streamingMessage(list: StoreMessage[]): StoreMessage | undefined {
    for (let i = list.length - 1; i >= 0; i -= 1) {
      const m = list[i]
      if (m && (m.phase === 'thinking' || m.phase === 'streaming')) return m
    }
    return undefined
  }

  // Structural changes: initial population, user sends, AI placeholder appears.
  // flush: 'post' so the DOM is patched before we query for message elements.
  watch(
    () => getMessages().length,
    (len, prevLen) => {
      const list = getMessages()

      if (!initialized) {
        if (len === 0) return
        initialized = true
        list.forEach((m) => handledIds.add(String(m.id)))
        // Rest at the newest message pinned to the top (the spacer gives room).
        // Retry briefly because the floating composer reserve and viewport
        // measurements can settle after Vue has patched the message DOM.
        const last = lastMessage(list)
        if (last) scheduleInitialJumpTo(last.id)
        return
      }

      cancelInitialJump()
      if (len <= (prevLen ?? 0)) return

      const last = lastMessage(list)
      if (!last) return

      if (last.is_user_author) {
        // The send is our unambiguous signal: allow the pending pin to fire.
        // Arming here (rather than when the AI placeholder appears) means any
        // scroll during the "thinking" wait cancels the pin.
        arm()
        lastAppendWasUserSend = true
        return
      }

      // A fresh streaming AI message that did NOT follow a user send (resend /
      // regenerate) is still user-initiated — arm it so its reply pins too. If
      // it did follow a send, `armed` already reflects any intervening scroll,
      // so we must not re-arm and override the user.
      const isNewStreamingAi =
        (last.phase === 'thinking' || last.phase === 'streaming') && !handledIds.has(String(last.id))
      if (!lastAppendWasUserSend && isNewStreamingAi) arm()
      lastAppendWasUserSend = false

      // The AI "thinking" placeholder appeared: minimal reveal.
      if (armed) {
        const target = findMessageEl(last.id)
        if (target) revealTop(target, REVEAL_MARGIN_PX)
      }
    },
    { immediate: true, flush: 'post' },
  )

  // First content chunk of the streaming AI message: the one-shot pin.
  watch(
    () => {
      const s = streamingMessage(getMessages())
      return s && s.content && s.content.length > 0 ? String(s.id) : null
    },
    (id) => {
      if (!id || handledIds.has(id)) return
      handledIds.add(id)
      if (!armed) return
      disarm()
      const target = findMessageEl(id)
      if (target && isInView(target)) pinToTop(target)
    },
    { flush: 'post' },
  )

  // Reroll: a streaming alternative arms on appearance and pins its PARENT
  // message's top on first content.
  if (getAlternatives) {
    watch(
      () => {
        const alts = getAlternatives()
        for (const parentId of Object.keys(alts)) {
          for (const alt of alts[parentId as unknown as number] ?? []) {
            if (alt.phase === 'streaming') {
              return {
                key: `alt-${alt.id}`,
                parentId,
                hasContent: !!(alt.content && alt.content.length > 0),
              }
            }
          }
        }
        return null
      },
      (cur) => {
        if (!cur) return
        if (!seenAltIds.has(cur.key)) {
          seenAltIds.add(cur.key)
          arm() // reroll is always user-initiated
        }
        if (!cur.hasContent || handledIds.has(cur.key)) return
        handledIds.add(cur.key)
        if (!armed) return
        disarm()
        const target = findMessageEl(cur.parentId)
        if (target && isInView(target)) pinToTop(target)
      },
      { deep: true, flush: 'post' },
    )
  }

  // Always-on cancel listeners (separate from the arm/disarm pin listeners) so a
  // scroll during either the reasoning or the content phase is recorded.
  onMounted(() => {
    const c = el()
    if (!c) return
    c.addEventListener('wheel', markStreamingScrollCancelled, { passive: true })
    c.addEventListener('touchmove', markStreamingScrollCancelled, { passive: true })
    c.addEventListener('keydown', onCancelKeyDown)
  })

  // Drop marks for messages that have left the list so the set cannot grow
  // unbounded. A temp→real id swap keeps the same key (client_key), so a
  // completed message's mark survives reconciliation.
  watch(
    () => getMessages().map(messageKey).join('|'),
    () => {
      const valid = new Set(getMessages().map(messageKey))
      cancelledScrollKeys.value = cancelledScrollKeys.value.filter((key) => valid.has(key))
    },
  )

  onBeforeUnmount(() => {
    cancelInitialJump()
    cancelTween()
    disarm()
    const c = el()
    if (c) {
      c.removeEventListener('wheel', markStreamingScrollCancelled)
      c.removeEventListener('touchmove', markStreamingScrollCancelled)
      c.removeEventListener('keydown', onCancelKeyDown)
    }
  })

  return { cancelTween, wasAutoScrollCancelled }
}
